import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Broker, brokerService } from '../services/brokerService';
import { Project, projectService } from '../services/projectService';
import '../css/standard-components.css';

const AddBroker: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { user } = useAuth();
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    address: '',
    projectId: 0
  });

  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/view/')) {
      setIsView(true);
    } else if (path.includes('/edit/')) {
      setIsEdit(true);
    }

    loadProjects();
    
    if (id && (isEdit || isView)) {
      loadBroker();
    }
  }, [id, isEdit, isView]);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects(true); // Get only active projects
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadBroker = async () => {
    try {
      setLoading(true);
      const broker = await brokerService.getBroker(parseInt(id!));
      setFormData({
        name: broker.name,
        mobile: broker.mobile,
        address: broker.address || '',
        projectId: broker.projectId
      });
    } catch (error) {
      console.error('Error loading broker:', error);
      alert('Error loading broker');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;

    try {
      setLoading(true);
      
      if (isEdit) {
        await brokerService.updateBroker(parseInt(id!), formData);
        alert('Broker updated successfully!');
      } else {
        await brokerService.createBroker(formData);
        alert('Broker created successfully!');
      }
      
      navigate('/brokers');
    } catch (error) {
      console.error('Error saving broker:', error);
      alert('Error saving broker');
    } finally {
      setLoading(false);
    }
  };

  const getPageTitle = () => {
    if (isView) return 'View Broker';
    if (isEdit) return 'Edit Broker';
    return 'Add Broker';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/brokers')}
        >
          Back to Brokers
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="standard-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Broker Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isView}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile *</label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                required
                disabled={isView}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="projectId">Project/Site *</label>
              <select
                id="projectId"
                name="projectId"
                value={formData.projectId}
                onChange={(e) => setFormData(prev => ({ ...prev, projectId: parseInt(e.target.value) }))}
                required
                disabled={isView}
                className="form-input"
              >
                <option value={0}>Select Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/brokers')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Broker' : 'Create Broker')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBroker;
